"use client";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icons } from "@/components/Icons";
import { Badge, Card, TopBar } from "@/components/ui";
import { useSavedCards } from "@/hooks/useSavedCards";

export function SavedPaymentsScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { cards, loading, error, removeCard } = useSavedCards();
  const [busyId, setBusyId] = useState<string | null>(null);

  async function onDelete(id: string) {
    if (!window.confirm(t("v2.payments.remove_confirm"))) return;
    setBusyId(id);
    try {
      await removeCard(id);
    } catch (err) {
      console.warn("[SavedPayments] delete failed", err);
      window.alert(t("v2.payments.remove_failed"));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div style={{ paddingBottom: 100 }}>
      <TopBar title={t("v2.payments.title")} onBack={() => navigate(-1)} />
      <div
        style={{
          padding: "8px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {loading && cards.length === 0 && (
          <div style={{ padding: 14, fontSize: 13, color: "var(--ink-3)" }}>
            {t("v2.payments.loading")}
          </div>
        )}
        {error && (
          <div
            role="alert"
            style={{
              fontSize: 13,
              color: "#b91c1c",
              padding: "10px 12px",
              background: "#fee2e2",
              borderRadius: 10,
            }}
          >
            {t("v2.payments.couldnt_load")}
          </div>
        )}
        {!loading && cards.length === 0 && (
          <div
            style={{
              padding: 14,
              fontSize: 13,
              color: "var(--ink-2)",
              textAlign: "center",
            }}
          >
            {t("v2.payments.none_saved")}
          </div>
        )}
        {cards.map((c) => {
          const brand = (c.brand ?? "").toLowerCase();
          return (
            <Card
              key={c.id}
              padding={14}
              style={{ display: "flex", alignItems: "center", gap: 12 }}
            >
              <div
                style={{
                  width: 44,
                  height: 30,
                  borderRadius: 6,
                  background:
                    brand === "visa"
                      ? "#1a1f71"
                      : brand === "amex"
                      ? "#016fd0"
                      : brand === "mastercard"
                      ? "#eb001b"
                      : "#374151",
                  color: "#fff",
                  display: "grid",
                  placeItems: "center",
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: 0.5,
                  flexShrink: 0,
                }}
              >
                {brand ? brand.toUpperCase().slice(0, 4) : "CARD"}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className="display" style={{ fontSize: 14, fontWeight: 700 }}>
                    •••• {c.last4}
                  </span>
                  {c.isDefault && <Badge tone="primary">{t("v2.common.default")}</Badge>}
                </div>
                {c.expiryMonth && c.expiryYear ? (
                  <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                    {t("v2.payments.expires", {
                      month: String(c.expiryMonth).padStart(2, "0"),
                      year: String(c.expiryYear).slice(-2),
                    })}
                  </div>
                ) : null}
              </div>
              <button
                onClick={() => onDelete(c.id)}
                disabled={busyId === c.id}
                aria-label={t("v2.payments.remove_aria")}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 999,
                  background: "transparent",
                  border: 0,
                  color: "var(--ink-3)",
                  cursor: busyId === c.id ? "default" : "pointer",
                  display: "grid",
                  placeItems: "center",
                  opacity: busyId === c.id ? 0.4 : 1,
                }}
              >
                <Icons.trash size={14} />
              </button>
            </Card>
          );
        })}

        <button
          onClick={() => navigate("/payments/new")}
          style={{
            padding: 14,
            borderRadius: "var(--radius-card)",
            border: "1px dashed var(--border-strong)",
            background: "transparent",
            color: "var(--ink-2)",
            fontWeight: 600,
            fontSize: 14,
            cursor: "pointer",
            fontFamily: "inherit",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
        >
          <Icons.plus size={16} /> {t("v2.payments.add_method")}
        </button>

        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: "var(--ink-3)",
            padding: "0 4px",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Icons.shield size={14} /> {t("v2.payments.secured")}
        </div>
      </div>
    </div>
  );
}
